/**
 * Unified API client for ClothedEye
 * Centralizes environment detection, URL construction, and JSON handling
 */

const isBrowser = typeof window !== 'undefined';

function isDevelopmentEnv() {
  if (!isBrowser) return false;
  const hostname = window.location && window.location.hostname;
  const port = window.location && window.location.port;
  const search = window.location && window.location.search;

  const forceLocal = /(?:[?&](?:api|local)=(?:local|1|true))(?:&|$)/i
    .test(search || '') ||
    (window.localStorage && window.localStorage.getItem('USE_LOCAL_API') === '1');

  return process.env.NODE_ENV === 'development' ||
    hostname === 'localhost' || hostname === '127.0.0.1' || port === '8888' ||
    forceLocal;
}

function getDevBaseForPath(path) {
  // Endpoints handled by top-level Netlify functions (no local-db prefix)
  const topLevel = [
    '/unified-stickers'
  ];
  if (topLevel.some(p => path === p || path.startsWith(`${p}/`))) {
    return '/.netlify/functions';
  }
  // Default to local-db function bundle for DB-backed endpoints
  return '/.netlify/functions/local-db';
}

function buildUrl(path, params) {
  const isDev = isDevelopmentEnv();

  // Ensure path starts with '/'
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (isDev) {
    const base = getDevBaseForPath(normalizedPath);
    const url = new URL(`${base}${normalizedPath}`, window.location.origin);
    if (params && typeof params === 'object') {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.append(key, String(value));
        }
      });
    }
    return url.toString();
  }

  // Production: always route through PHP with ?path=
  const base = 'https://www.clothedeye.com/api/index.php';
  const url = new URL(base);
  // Use encodeURI to preserve slashes in the path
  url.searchParams.set('path', encodeURI(normalizedPath));
  if (params && typeof params === 'object') {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, String(value));
      }
    });
  }
  return url.toString();
}

async function requestJson({
  path,
  method = 'GET',
  params,
  body,
  headers = {},
  retries = 1,
  retryDelayMs = 250
}) {
  const url = buildUrl(path, params);

  const fetchOptions = {
    method,
    headers: {
      'Accept': 'application/json',
      ...headers
    }
  };

  if (body !== undefined) {
    fetchOptions.headers['Content-Type'] = 'application/json';
    fetchOptions.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(url, fetchOptions);
  } catch (networkError) {
    if (retries > 0) {
      await new Promise(r => setTimeout(r, retryDelayMs));
      return requestJson({
        path,
        method,
        params,
        body,
        headers,
        retries: retries - 1,
        retryDelayMs: retryDelayMs * 2
      });
    }
    throw networkError;
  }

  // Optional retry on transient server errors
  if (!response.ok && [502, 503, 504].includes(response.status) && retries > 0) {
    await new Promise(r => setTimeout(r, retryDelayMs));
    return requestJson({
      path,
      method,
      params,
      body,
      headers,
      retries: retries - 1,
      retryDelayMs: retryDelayMs * 2
    });
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP ${response.status}: ${response.statusText} — ${text.substring(0, 200)}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.toLowerCase().includes('application/json')) {
    const text = await response.text();
    throw new Error(
      `Non-JSON response (content-type=${contentType}) at ${url}: ` +
      `${text.substring(0, 200)}`
    );
  }

  return response.json();
}

const apiClient = {
  buildUrl,
  requestJson
};

export default apiClient;


